// Take the current RoomState plus one new event, and return an updated RoomState
import { GameElementType, ElementActionType, MediaActionType, SenderRoleType, } from '../types';
import { produce } from 'immer';
import { createEmptyRoomState } from './roomState';
import { getGameElementCollectionKey } from '../utilities/getGameElementCollectionKey';
function getElementByType(state, elementType, elementId) {
    switch (elementType) {
        case GameElementType.TURN:
            return state.turns[elementId];
        case GameElementType.PROMPT:
            return state.prompts[elementId];
        case GameElementType.ANSWER:
            return state.answers[elementId];
        case GameElementType.VOTE:
            return state.votes[elementId];
    }
}
/**
 * Helper to remove element from parent's child array
 */
function removeFromParentArray(draft, elementType, parentId, elementId) {
    if (elementType === GameElementType.VOTE) {
        const parent = draft.answers[parentId];
        if (parent?.voteIds) {
            parent.voteIds = parent.voteIds.filter((id) => id !== elementId);
        }
    }
    else if (elementType === GameElementType.ANSWER) {
        const parent = draft.prompts[parentId];
        if (parent?.answerIds) {
            parent.answerIds = parent.answerIds.filter((id) => id !== elementId);
        }
    }
    else if (elementType === GameElementType.PROMPT) {
        const parent = draft.turns[parentId];
        if (parent?.promptIds) {
            parent.promptIds = parent.promptIds.filter((id) => id !== elementId);
        }
    }
}
/**
 * Helper to add element to parent's child array
 */
function addToParentArray(draft, elementType, parentId, elementId) {
    if (elementType === GameElementType.VOTE) {
        const parent = draft.answers[parentId];
        if (parent?.voteIds) {
            parent.voteIds.push(elementId);
        }
    }
    else if (elementType === GameElementType.ANSWER) {
        const parent = draft.prompts[parentId];
        if (parent?.answerIds) {
            parent.answerIds.push(elementId);
        }
    }
    else if (elementType === GameElementType.PROMPT) {
        const parent = draft.turns[parentId];
        if (parent?.promptIds) {
            parent.promptIds.push(elementId);
        }
    }
}
function handleGameElementOperation(draft, elementType, elementId, operation, content, contentGroup, parentElementId, assignedPlayerId, assignedTeamId) {
    const collection = getGameElementCollectionKey(elementType);
    const elements = draft[collection];
    if (operation === ElementActionType.CREATE) {
        switch (elementType) {
            case GameElementType.TURN:
                elements[elementId] = {
                    elementId: elementId,
                    type: elementType,
                    content: content ?? '',
                    contentGroup: contentGroup ?? null,
                    promptIds: [],
                    assignedPlayerId: assignedPlayerId,
                    assignedTeamId: assignedTeamId,
                };
                break;
            case GameElementType.PROMPT:
                elements[elementId] = {
                    elementId: elementId,
                    type: elementType,
                    content: content ?? '',
                    contentGroup: contentGroup ?? null,
                    parentId: parentElementId || '',
                    answerIds: [],
                    assignedPlayerId: assignedPlayerId,
                    assignedTeamId: assignedTeamId,
                };
                break;
            case GameElementType.ANSWER:
                elements[elementId] = {
                    elementId: elementId,
                    type: elementType,
                    content: content ?? '',
                    contentGroup: contentGroup ?? null,
                    parentId: parentElementId || '',
                    voteIds: [],
                    assignedPlayerId: assignedPlayerId,
                    assignedTeamId: assignedTeamId,
                };
                break;
            case GameElementType.VOTE:
                elements[elementId] = {
                    elementId: elementId,
                    type: elementType,
                    content: content ?? '',
                    contentGroup: contentGroup ?? null,
                    parentId: parentElementId || '',
                    assignedPlayerId: assignedPlayerId,
                    assignedTeamId: assignedTeamId,
                };
                break;
        }
    }
    else if (operation === ElementActionType.UPDATE) {
        const element = getElementByType(draft, elementType, elementId);
        if (!element) {
            throw new Error(`Element ${elementId} of type ${elementType} not found`);
        }
        // Handle parentId setting (for reparenting elements)
        if (parentElementId !== undefined && 'parentId' in element) {
            const oldParentId = element.parentId;
            // Only process if parentId is actually changing
            if (oldParentId !== parentElementId) {
                // Remove from old parent's child array (if had a parent)
                if (oldParentId) {
                    removeFromParentArray(draft, elementType, oldParentId, elementId);
                }
                // Set the new parentId
                elements[elementId].parentId = parentElementId;
                // Add to new parent's child array
                addToParentArray(draft, elementType, parentElementId, elementId);
            }
        }
        if (content !== undefined) {
            elements[elementId].content = content;
        }
        if (contentGroup !== undefined) {
            elements[elementId].contentGroup = contentGroup;
        }
        if (assignedPlayerId !== undefined) {
            elements[elementId].assignedPlayerId = assignedPlayerId;
        }
        if (assignedTeamId !== undefined) {
            elements[elementId].assignedTeamId = assignedTeamId;
        }
    }
    else if (operation === ElementActionType.REMOVE) {
        delete elements[elementId];
    }
}
/**
 * Apply a single event to the current room state and return the updated state
 * @param state Current room state
 * @param event Event to apply
 * @returns Updated room state
 */
export function applyEvent(state, event) {
    // Handle initial room creation
    if (!state && event.type === 'RoomCreated') {
        return handleRoomCreated(event);
    }
    // Validate state exists for all other events
    if (!state) {
        throw new Error('Cannot apply event to non-existent state');
    }
    // Validate event version
    if (event.version !== state.lastEventVersion + 1) {
        throw new Error(`Invalid event version. Expected ${state.lastEventVersion + 1}, got ${event.version}`);
    }
    // Apply event based on type
    switch (event.type) {
        // Room Management Events
        case 'GameStarted':
            return handleGameStarted(state, event);
        case 'GameEnded':
            return handleGameEnded(state, event);
        case 'RoomClosed':
            return handleRoomClosed(state, event);
        case 'PlayerJoined':
            return handlePlayerJoined(state, event);
        case 'PlayerUpdated':
            return handlePlayerUpdated(state, event);
        case 'PlayerEliminated':
            return handlePlayerEliminated(state, event);
        case 'PlayerRemoved':
            return handlePlayerRemoved(state, event);
        case 'PlayerAddedToTeam':
            return handlePlayerAddedToTeam(state, event);
        case 'PlayerRemovedFromTeam':
            return handlePlayerRemovedFromTeam(state, event);
        case 'PlayerChangedTeam':
            return handlePlayerChangedTeam(state, event);
        case 'TeamCreated':
            return handleTeamCreated(state, event);
        case 'TeamUpdated':
            return handleTeamUpdated(state, event);
        case 'TeamDisbanded':
            return handleTeamDisbanded(state, event);
        case 'TeamEliminated':
            return handleTeamEliminated(state, event);
        // Gameplay Events
        case 'GameElementCreated':
            return handleGameElementCreated(state, event);
        case 'GameElementUpdated':
            return handleGameElementUpdated(state, event);
        case 'GameElementRemoved':
            return handleGameElementRemoved(state, event);
        case 'InfoElementCreated':
            return handleInfoElementCreated(state, event);
        case 'InfoElementUpdated':
            return handleInfoElementUpdated(state, event);
        case 'InfoElementRemoved':
            return handleInfoElementRemoved(state, event);
        case 'MediaReady':
            return handleMediaReady(state, event);
        case 'MediaRequested':
            return handleMediaRequested(state, event);
        case 'MediaStarted':
            return handleMediaStarted(state, event);
        case 'MediaCompleted':
            return handleMediaCompleted(state, event);
        case 'InteractionWindowStarted':
            return handleInteractionWindowStarted(state, event);
        case 'InteractionWindowPaused':
            return handleInteractionWindowPaused(state, event);
        case 'InteractionWindowResumed':
            return handleInteractionWindowResumed(state, event);
        case 'InteractionWindowEnded':
            return handleInteractionWindowEnded(state, event);
        case 'InteractionWindowWarned':
            return handleInteractionWindowWarned(state, event);
        case 'ScoreElement':
            return handleScoreElement(state, event);
        case 'PlayerInput':
            return handlePlayerInput(state, event);
        case 'PlayerGuess':
            return handlePlayerGuess(state, event);
        // System Events
        case 'CommandRejected':
        case 'SystemNotification':
            // System events don't modify state
            return state;
        case 'RoomCreated':
            throw new Error('Cannot apply RoomCreated to an existing room state');
        default: {
            const unexpected = event;
            throw new Error(`Unknown event type: ${JSON.stringify(unexpected)}`);
        }
    }
}
// ============= Room Management Event Handlers =============
function handleRoomCreated(event) {
    // Create initial room state with proper game and host info
    return createEmptyRoomState(event.roomId, event.roomCode, event.host, event.gameConfig);
}
function handleGameStarted(state, event) {
    return produce(state, (draft) => {
        draft.status = 'playing';
        draft.lastEventVersion = event.version;
        draft.gameConfig = event.gameConfig;
        draft.turnsRemaining = event.gameConfig.gameTotalTurns;
    });
}
function handleGameEnded(state, event) {
    return produce(state, (draft) => {
        draft.status = 'ended';
        draft.lastEventVersion = event.version;
    });
}
function handleRoomClosed(state, event) {
    return produce(state, (draft) => {
        draft.status = 'closed';
        draft.lastEventVersion = event.version;
    });
}
function handlePlayerJoined(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.players[event.player.playerId] = {
            ...event.player,
            // Initialize room-specific defaults
            isEliminated: false,
            scoreTurn: 0,
            scoreGame: 0,
            role: event.player.role || SenderRoleType.PLAYER, // Use role from event or default to PLAYER
            teamId: event.player.teamId || undefined,
        };
    });
}
function handlePlayerUpdated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.players[event.player.playerId] = {
            ...draft.players[event.player.playerId],
            ...event.player,
        };
    });
}
function handlePlayerEliminated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.players[event.player.playerId].isEliminated = true;
        // If the eliminated player is on a team, check if all players on that team are now eliminated
        const eliminatedPlayer = draft.players[event.player.playerId];
        if (eliminatedPlayer.teamId) {
            const team = draft.teams[eliminatedPlayer.teamId];
            if (team && !team.isEliminated) {
                // Check if all players on the team are eliminated
                const allPlayersEliminated = team.teamPlayerIds.every((playerId) => draft.players[playerId]?.isEliminated === true);
                if (allPlayersEliminated) {
                    team.isEliminated = true;
                }
            }
        }
    });
}
function handlePlayerRemoved(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        const removedPlayerId = event.player.playerId;
        // Keep interaction window wait-list consistent when a player is removed.
        draft.windowWaitingFor = (draft.windowWaitingFor || []).filter((playerId) => playerId !== removedPlayerId);
        // Remove player from all teams to avoid stale references.
        Object.values(draft.teams).forEach((team) => {
            team.teamPlayerIds = team.teamPlayerIds.filter((playerId) => playerId !== removedPlayerId);
        });
        delete draft.players[removedPlayerId];
    });
}
function handlePlayerAddedToTeam(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.players[event.player.playerId].teamId = event.teamId;
        draft.teams[event.teamId].teamPlayerIds.push(event.player.playerId);
    });
}
function handlePlayerRemovedFromTeam(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.players[event.player.playerId].teamId = undefined;
        draft.teams[event.teamId].teamPlayerIds = draft.teams[event.teamId].teamPlayerIds.filter((playerId) => playerId !== event.player.playerId);
    });
}
function handlePlayerChangedTeam(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        // Remove player from old team if they were on one
        if (event.fromTeamId && draft.teams[event.fromTeamId]) {
            draft.teams[event.fromTeamId].teamPlayerIds = draft.teams[event.fromTeamId].teamPlayerIds.filter((playerId) => playerId !== event.player.playerId);
        }
        // Add player to new team
        draft.teams[event.toTeamId].teamPlayerIds.push(event.player.playerId);
        // Update player's teamId
        draft.players[event.player.playerId].teamId = event.toTeamId;
    });
}
function handleTeamCreated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.teams[event.team.teamId] = event.team;
    });
}
function handleTeamUpdated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.teams[event.team.teamId] = {
            ...draft.teams[event.team.teamId],
            ...event.team,
        };
    });
}
function handleTeamDisbanded(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        delete draft.teams[event.team.teamId];
    });
}
/**
 * Handle team elimination
 * @param state Current room state
 * @param event Team eliminated event
 * @returns Updated room state
 */
function handleTeamEliminated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.teams[event.team.teamId].isEliminated = true;
    });
}
// ============= Gameplay Event Handlers =============
function handleGameElementCreated(state, event) {
    return produce(state, (draft) => {
        handleGameElementOperation(draft, event.elementType, event.elementId, ElementActionType.CREATE, event.content, event.contentGroup, event.parentElementId, event.assignedPlayerId, event.assignedTeamId);
        // Decrement turnsRemaining if a new Turn is created
        if (event.elementType === GameElementType.TURN &&
            typeof draft.turnsRemaining === 'number') {
            draft.turnsRemaining = Math.max(0, draft.turnsRemaining - 1);
            // Reset all players' scoreTurn to 0 for the new turn
            Object.values(draft.players).forEach((player) => {
                player.scoreTurn = 0;
            });
        }
        // If parentElementId is provided, update the parent's child array
        if (event.parentElementId) {
            addToParentArray(draft, event.elementType, event.parentElementId, event.elementId);
        }
        draft.lastEventVersion = event.version;
    });
}
function handleGameElementUpdated(state, event) {
    return produce(state, (draft) => {
        handleGameElementOperation(draft, event.elementType, event.elementId, ElementActionType.UPDATE, event.content, event.contentGroup, event.parentElementId, event.assignedPlayerId, event.assignedTeamId);
        draft.lastEventVersion = event.version;
    });
}
function handleGameElementRemoved(state, event) {
    return produce(state, (draft) => {
        const elementId = event.elementId;
        const elementType = event.elementType;
        // Get the element before removing it to access its relationships
        const element = getElementByType(draft, elementType, elementId);
        if (!element) {
            // Element doesn't exist, nothing to remove
            draft.lastEventVersion = event.version;
            return;
        }
        // 1. Clean up parent's child array - remove this element's ID from parent
        if (element.parentId) {
            removeFromParentArray(draft, elementType, element.parentId, elementId);
        }
        // 2. Clean up children's parentId - clear parentId from child elements
        if (elementType === GameElementType.TURN && 'promptIds' in element) {
            // Clear parentId from all child prompts
            element.promptIds.forEach((promptId) => {
                if (draft.prompts[promptId]) {
                    draft.prompts[promptId].parentId = '';
                }
            });
        }
        else if (elementType === GameElementType.PROMPT &&
            'answerIds' in element) {
            // Clear parentId from all child answers
            element.answerIds.forEach((answerId) => {
                if (draft.answers[answerId]) {
                    draft.answers[answerId].parentId = '';
                }
            });
        }
        else if (elementType === GameElementType.ANSWER && 'voteIds' in element) {
            // Clear parentId from all child votes
            element.voteIds.forEach((voteId) => {
                if (draft.votes[voteId]) {
                    draft.votes[voteId].parentId = '';
                }
            });
        }
        // 3. Remove from current interaction window elementIds if present
        if (draft.currentInteractionWindow &&
            draft.currentInteractionWindow.elementIds) {
            draft.currentInteractionWindow.elementIds =
                draft.currentInteractionWindow.elementIds.filter((id) => id !== elementId);
        }
        // 4. Finally, remove the element itself
        handleGameElementOperation(draft, elementType, elementId, ElementActionType.REMOVE);
        // Element removal doesn't change player participation status for the windowWaitingFor
        // Players remain in windowWaitingFor until they submit player input for the element
        // or enough votes are created according to the specific game recipe
        draft.lastEventVersion = event.version;
    });
}
function handleInfoElementCreated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.infoElements[event.infoId] = {
            infoId: event.infoId,
            type: event.infoType,
            content: event.content || '',
            elementId: event.elementId,
        };
    });
}
function handleInfoElementUpdated(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.infoElements[event.infoId] = {
            ...draft.infoElements[event.infoId],
            content: event.content,
            elementId: event.elementId,
        };
    });
}
function handleInfoElementRemoved(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        delete draft.infoElements[event.infoId];
    });
}
function handleMediaRequested(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.mediaElements[event.mediaId] = {
            mediaId: event.mediaId,
            mediaType: event.mediaType,
            mediaAction: MediaActionType.REQUEST,
            elementId: event.elementId,
            ...(event.content && { content: event.content }),
            ...(event.imageUrl && { imageUrl: event.imageUrl }),
            ...(event.musicUrl && { musicUrl: event.musicUrl }),
            ...(event.sfxUrl && { sfxUrl: event.sfxUrl }),
        };
    });
}
function handleMediaReady(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.mediaElements[event.mediaId] = {
            mediaId: event.mediaId,
            mediaType: event.mediaType,
            mediaAction: MediaActionType.READY,
            elementId: event.elementId,
            ...(event.content && { content: event.content }),
            ...(event.imageUrl && { imageUrl: event.imageUrl }),
            ...(event.musicUrl && { musicUrl: event.musicUrl }),
            ...(event.sfxUrl && { sfxUrl: event.sfxUrl }),
        };
    });
}
function handleMediaStarted(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.mediaElements[event.mediaId] = {
            mediaId: event.mediaId,
            mediaType: event.mediaType,
            mediaAction: MediaActionType.STARTED,
            elementId: event.elementId,
            ...(event.content && { content: event.content }),
            ...(event.imageUrl && { imageUrl: event.imageUrl }),
            ...(event.musicUrl && { musicUrl: event.musicUrl }),
            ...(event.sfxUrl && { sfxUrl: event.sfxUrl }),
        };
    });
}
function handleMediaCompleted(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        draft.mediaElements[event.mediaId] = {
            mediaId: event.mediaId,
            mediaType: event.mediaType,
            mediaAction: MediaActionType.COMPLETED,
            elementId: event.elementId,
            ...(event.content && { content: event.content }),
            ...(event.imageUrl && { imageUrl: event.imageUrl }),
            ...(event.musicUrl && { musicUrl: event.musicUrl }),
            ...(event.sfxUrl && { sfxUrl: event.sfxUrl }),
        };
    });
}
function handleInteractionWindowStarted(state, event) {
    return produce(state, (draft) => {
        draft.currentInteractionWindow = {
            elementType: event.elementType,
            elementIds: event.elementIds,
            endTime: event.endTime,
            windowId: event.windowId,
            windowStatus: 'opened',
            unlimitedPlayerInput: event.unlimitedPlayerInput,
        };
        // If unlimitedPlayerInput is true, set windowWaitingFor to empty array
        // Otherwise, populate with assigned players (existing behavior)
        if (event.unlimitedPlayerInput === true) {
            draft.windowWaitingFor = [];
        }
        else {
            // Extract assigned player IDs from elements
            const collection = getGameElementCollectionKey(event.elementType);
            const elements = draft[collection];
            const assignedPlayerIds = event.elementIds
                .map((id) => elements[id]?.assignedPlayerId)
                .filter((playerId) => playerId !== undefined);
            // Only use assignments (validation done earlier that there are assignments prior to opening the window)
            draft.windowWaitingFor = assignedPlayerIds;
        }
        draft.lastEventVersion = event.version;
    });
}
function handleInteractionWindowPaused(state, event) {
    return produce(state, (draft) => {
        if (draft.currentInteractionWindow) {
            draft.currentInteractionWindow.windowStatus = 'paused';
        }
        draft.lastEventVersion = event.version;
    });
}
function handleInteractionWindowResumed(state, event) {
    return produce(state, (draft) => {
        if (draft.currentInteractionWindow) {
            draft.currentInteractionWindow.windowStatus = 'opened';
            draft.currentInteractionWindow.endTime = event.endTime;
        }
        draft.lastEventVersion = event.version;
    });
}
function handleInteractionWindowEnded(state, event) {
    return produce(state, (draft) => {
        if (draft.currentInteractionWindow) {
            draft.currentInteractionWindow.windowStatus = 'closed';
        }
        // Clear windowWaitingFor when the interaction window ends
        draft.windowWaitingFor = [];
        draft.lastEventVersion = event.version;
    });
}
function handleInteractionWindowWarned(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
    });
}
function handleScoreElement(state, event) {
    return produce(state, (draft) => {
        // Store the score element in the scoreElements object
        draft.scoreElements[event.scoreId] = {
            content: event.content,
            elementId: event.elementId,
            scoreId: event.scoreId,
        };
        // 1. Update each player's scoreTurn and scoreGame
        // Accumulate scores to allow multiple score elements per turn
        Object.entries(event.content).forEach(([playerId, score]) => {
            const player = draft.players[playerId];
            if (player) {
                player.scoreTurn = (player.scoreTurn || 0) + score;
                player.scoreGame += score;
            }
        });
        // 2. Calculate team scores (sum of player scores)
        const teamTurnScores = {};
        const teamGameScores = {};
        Object.values(draft.players).forEach((player) => {
            if (player.teamId) {
                teamTurnScores[player.teamId] =
                    (teamTurnScores[player.teamId] || 0) + player.scoreTurn;
                teamGameScores[player.teamId] =
                    (teamGameScores[player.teamId] || 0) + player.scoreGame;
            }
        });
        // 3. Update team objects with calculated scores
        Object.entries(teamTurnScores).forEach(([teamId, score]) => {
            const team = draft.teams[teamId];
            if (team) {
                team.scoreTurn = score;
            }
        });
        Object.entries(teamGameScores).forEach(([teamId, score]) => {
            const team = draft.teams[teamId];
            if (team) {
                team.scoreGame = score;
            }
        });
        // 4. Format playerScoreboard: [playerName: scoreTurn, ...]
        const playerScoreboardArr = Object.values(draft.players).map((player) => `${player.playerName} has ${player.scoreTurn} points`);
        draft.playerScoreboard = `${playerScoreboardArr.join(', ')}`;
        // 5. Format playerLeaderboard: [playerName: scoreGame, ...]
        const playerLeaderboardArr = Object.values(draft.players).map((player) => `${player.playerName} has ${player.scoreGame} points`);
        draft.playerLeaderboard = `${playerLeaderboardArr.join(', ')}`;
        // 6. Format teamScoreboard: "Team A has 200 points, Team B has 0 points"
        const teamScoreboardArr = Object.entries(teamTurnScores)
            .map(([teamId, score]) => {
            const team = draft.teams[teamId];
            return team ? `${team.teamName} has ${score} points` : null;
        })
            .filter(Boolean);
        draft.teamScoreboard = `${teamScoreboardArr.join(', ')}`;
        // 7. Format teamLeaderboard: "Team A has 666 points, Team B has 0 points"
        const teamLeaderboardArr = Object.entries(teamGameScores)
            .map(([teamId, score]) => {
            const team = draft.teams[teamId];
            return team ? `${team.teamName} has ${score} points` : null;
        })
            .filter(Boolean);
        draft.teamLeaderboard = `${teamLeaderboardArr.join(', ')}`;
        draft.lastEventVersion = event.version;
    });
}
function handlePlayerInput(state, event) {
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
        // Upsert the content of the element
        const collection = getGameElementCollectionKey(event.elementType);
        const elements = draft[collection];
        if (elements[event.elementId]) {
            elements[event.elementId].content = event.content;
            // Update contentGroup if provided
            if (event.contentGroup !== undefined &&
                'contentGroup' in elements[event.elementId]) {
                elements[event.elementId].contentGroup = event.contentGroup;
            }
            // Handle reparenting if parentElementId is provided
            if (event.parentElementId !== undefined &&
                'parentId' in elements[event.elementId]) {
                const element = elements[event.elementId];
                const oldParentId = element.parentId;
                // Only process if parentId is actually changing
                if (oldParentId !== event.parentElementId) {
                    // Remove from old parent's child array (if had a parent)
                    if (oldParentId) {
                        removeFromParentArray(draft, event.elementType, oldParentId, event.elementId);
                    }
                    // Set the new parentId
                    element.parentId = event.parentElementId;
                    // Add to new parent's child array
                    addToParentArray(draft, event.elementType, event.parentElementId, event.elementId);
                }
            }
        }
        // Only remove player from windowWaitingFor if all their assigned elements in the window have non-empty content
        // Skip this logic when unlimitedPlayerInput is true (window only closes on timer expiration)
        if (draft.currentInteractionWindow &&
            draft.currentInteractionWindow.elementType === event.elementType &&
            draft.currentInteractionWindow.elementIds.includes(event.elementId) &&
            draft.currentInteractionWindow.unlimitedPlayerInput !== true) {
            // Find all elements in the window assigned to this player
            const assignedElementIds = draft.currentInteractionWindow.elementIds.filter((id) => {
                const el = elements[id];
                return el && el.assignedPlayerId === event.playerId;
            });
            // Check if all assigned elements have non-empty content
            const allSubmitted = assignedElementIds.every((id) => elements[id] && elements[id].content !== '');
            if (allSubmitted) {
                draft.windowWaitingFor = (draft.windowWaitingFor || []).filter((id) => id !== event.playerId);
            }
        }
    });
}
function handlePlayerGuess(state, event) {
    // PlayerGuess events don't modify room state - they forward to game recipes for processing
    // We only update the lastEventVersion to maintain event sequencing
    return produce(state, (draft) => {
        draft.lastEventVersion = event.version;
    });
}
//# sourceMappingURL=roomEventApplier.js.map